import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface ValidationRule {
  id: string;
  name: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'passed' | 'failed' | 'warning' | 'running' | 'pending' | 'disabled';
  progress?: number;
  rule_type: string;
  conditions: any;
  actions: any;
  is_active: boolean;
}

export interface ActiveValidation {
  id: string;
  billId: string;
  ruleId: string;
  stage: string;
  status: 'running' | 'completed' | 'failed';
  message: string;
  progress: number;
  started_at: string;
  details?: any;
}

export interface ValidationStats {
  total: number;
  completed: number;
  passed: number;
  failed: number;
  warnings: number;
  running: number;
}

export const useValidationEngine = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isRunning, setIsRunning] = useState(false);

  // Fetch validation rules
  const validationRulesQuery = useQuery({
    queryKey: ['validation-rules', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('processing_rules')
        .select('*')
        .eq('user_id', user.id)
        .eq('rule_type', 'validation')
        .order('priority', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(rule => ({
        id: rule.id,
        name: rule.name,
        description: rule.description || '',
        severity: 'medium' as const,
        status: rule.is_active ? 'pending' as const : 'disabled' as const,
        progress: 0,
        rule_type: rule.rule_type,
        conditions: rule.conditions,
        actions: rule.actions,
        is_active: rule.is_active
      }));
    },
    enabled: !!user?.id,
  });

  // Fetch active validations
  const activeValidationsQuery = useQuery({
    queryKey: ['active-validations', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('processing_logs')
        .select('*')
        .eq('user_id', user.id)
        .eq('stage', 'validation')
        .eq('status', 'running')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      
      return (data || []).map(log => ({
        id: log.id,
        billId: log.bill_id,
        ruleId: log.id,
        stage: log.stage,
        status: log.status as 'running' | 'completed' | 'failed',
        message: (log.details as any)?.message || 'Running validation...',
        progress: (log.details as any)?.progress || 0,
        started_at: log.created_at,
        details: log.details
      }));
    },
    enabled: !!user?.id,
    refetchInterval: isRunning ? 2000 : false,
  });

  // Calculate validation stats
  const validationStats: ValidationStats = {
    total: validationRulesQuery.data?.length || 0,
    completed: 0, // Will be updated when we implement actual validation results
    passed: 0,
    failed: 0,
    warnings: 0,
    running: activeValidationsQuery.data?.length || 0,
  };

  // Start validation mutation
  const startValidationMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('start-validation-engine', {
        body: { user_id: user.id }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setIsRunning(true);
      toast({ title: "Validation engine started", description: "Running validation rules on processed bills" });
      queryClient.invalidateQueries({ queryKey: ['active-validations'] });
    },
    onError: (error) => {
      toast({ title: "Failed to start validation", description: error.message, variant: "destructive" });
    },
  });

  // Pause validation mutation
  const pauseValidationMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('pause-validation-engine', {
        body: { user_id: user.id }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setIsRunning(false);
      toast({ title: "Validation engine paused", description: "Validation has been paused" });
      queryClient.invalidateQueries({ queryKey: ['active-validations'] });
    },
    onError: (error) => {
      toast({ title: "Failed to pause validation", description: error.message, variant: "destructive" });
    },
  });

  // Get validation results
  const getValidationResults = async (billId: string) => {
    if (!user?.id) return [];

    const { data, error } = await supabase
      .from('processing_logs')
      .select('*')
      .eq('user_id', user.id)
      .eq('bill_id', billId)
      .eq('stage', 'validation')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  };

  // Create default validation rules if none exist
  useEffect(() => {
    const createDefaultRules = async () => {
      if (!user?.id || !validationRulesQuery.data || validationRulesQuery.data.length > 0) return;

      const defaultRules = [
        {
          name: 'Amount Validation',
          description: 'Validates bill amounts are within expected ranges',
          rule_type: 'validation',
          conditions: { field: 'amount', operator: 'range', min: 0, max: 10000 },
          actions: { on_fail: 'flag_for_review' },
          priority: 90,
          is_active: true,
          user_id: user.id
        },
        {
          name: 'Date Validation',
          description: 'Ensures bill dates are valid and recent',
          rule_type: 'validation',
          conditions: { field: 'bill_date', operator: 'date_range', days_back: 365 },
          actions: { on_fail: 'flag_for_review' },
          priority: 85,
          is_active: true,
          user_id: user.id
        },
        {
          name: 'Account Number Format',
          description: 'Validates account number format consistency',
          rule_type: 'validation',
          conditions: { field: 'account_number', operator: 'format', pattern: '^[A-Z0-9-]+$' },
          actions: { on_fail: 'auto_correct' },
          priority: 80,
          is_active: true,
          user_id: user.id
        }
      ];

      for (const rule of defaultRules) {
        await supabase.from('processing_rules').insert(rule);
      }

      queryClient.invalidateQueries({ queryKey: ['validation-rules'] });
    };

    createDefaultRules();
  }, [user?.id, validationRulesQuery.data, queryClient]);

  return {
    validationRules: validationRulesQuery.data || [],
    activeValidations: activeValidationsQuery.data || [],
    validationStats,
    isRunning,
    isLoading: validationRulesQuery.isLoading || activeValidationsQuery.isLoading,
    error: validationRulesQuery.error || activeValidationsQuery.error,
    startValidation: () => startValidationMutation.mutate(),
    pauseValidation: () => pauseValidationMutation.mutate(),
    getValidationResults,
    refetch: () => {
      validationRulesQuery.refetch();
      activeValidationsQuery.refetch();
    }
  };
};